import { modifier } from 'ember-modifier';
import { playMotion, stopMotion } from '../lib/nv-motion.js';

/**
 * MAP (in-file navigation graph — keep in sync with the code):
 *   role:      modifier — «UI nuova» entrance of a row ADDED to a list after it rendered (M19 item 22: a payment
 *              recorded on an invoice appears in its money card). Put it on the list CONTAINER:
 *              `<div {{nv-enter-on-add '.nv-dpay'}}>`.
 *   behaviour: the children present when the container is inserted never animate (first render, reloads that
 *              re-create the container). Later element children matching the selector play lib/nv-motion 'enter'
 *              once — only when at most MAX_BATCH arrive together: a list refilled by a reload or a filter is a new
 *              page of rows, not an addition.
 *   args:      positional[0] CSS selector of a row (optional: every element child when omitted)
 *   uses:      ember-modifier (function), MutationObserver (childList of the container only), lib/nv-motion
 *   css:       app/styles/nuova/_motion.scss (.nv-flash--enter)
 *   gotchas:   removals are not animated (Glimmer removes the node at once; a delayed removal would keep a dead row
 *              on screen). Classic UI: no class is ever added (lib/nv-motion isNuovaDom).
 *   tests:     tests/integration/modifiers/nv-enter-on-add-test.js
 */
const MAX_BATCH = 2;
var nvEnterOnAdd = modifier((element, [selector]) => {
  if (typeof MutationObserver === 'undefined') return undefined;
  const played = new Set();
  const observer = new MutationObserver(records => {
    const added = [];
    for (const record of records) {
      for (const node of record.addedNodes) {
        if (node.nodeType !== 1) continue;
        if (selector && !node.matches(selector)) continue;
        added.push(node);
      }
    }
    if (!added.length || added.length > MAX_BATCH) return;
    for (const node of added) {
      if (playMotion(node, 'enter')) played.add(node);
    }
  });
  observer.observe(element, {
    childList: true
  });
  return () => {
    observer.disconnect();
    for (const node of played) stopMotion(node);
  };
});

export { nvEnterOnAdd as default };
//# sourceMappingURL=nv-enter-on-add.js.map
